import { ApiResponse, GlobalStore, UserStore } from '@roc/feature-app-core';
import { isElectronApp, isElectronAppLocalHost, isMobileApp, isMobileAppLocalHost } from '@roc/feature-utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import SockJS from 'sockjs-client';
import Stomp, { Subscription } from 'stompjs';
import { WebSocketService } from '../services/webSocketService';
import { _getBaseStoreInstance } from './_baseStore';
import { UserOnlineStatusNotification, ConversationUpdatesNotification, CallInviteNotification, CallStatusNotification } from '@roc/feature-types';

export enum SubscriptionTopic {
  TOPIC_CONVERSATION_MESSAGE_UPDATES = '/user/topic/private-messages',
  TOPIC_USER_ONLINE_STATUS_UPDATES = '/user/topic/user-online-status-updates',
  // LoanChat Meet.
  TOPIC_CALL_INVITE = '/user/topic/call-invite',
  TOPIC_CALL_STATUS = '/user/topic/call-status',
}

interface SubscriptionRegistration<T> {
  subscriptionTopic: SubscriptionTopic,
  callBack: (messageData: T) => void;
}

type SubscriptionRegistrationType = SubscriptionRegistration<
  | ConversationUpdatesNotification
  | UserOnlineStatusNotification
  | CallInviteNotification
  | CallStatusNotification
>;

export class WebSocketStore {

  private globalStore: GlobalStore;
  private userStore: UserStore;
  private webSocketService: WebSocketService;
  private stompClientPromise: Promise<Stomp.Client>;
  private timeoutId: any;
  private subscriptions: Record<SubscriptionTopic | string, Subscription>;
  private subscriptionRegistrations: Record<SubscriptionTopic | string, SubscriptionRegistrationType>;

  onlineUsers: Record<number, boolean>;
  /**
   * Who can be rung, as last answered by the server. Separate from onlineUsers on purpose: that
   * one says "signed in somewhere", this one says "could pick up". A call is only ever raised in
   * the LoanChat desktop app, so a colleague on the web is online and still unreachable.
   *
   * Point-in-time rather than live. Nothing pushes callability over the socket, so this is filled
   * on demand — by the UI that is about to offer a call — and may be a few seconds stale. That is
   * the right trade here: the cost of being wrong is one unanswered ring, and the alternative is
   * a subscription for a value almost nobody looks at.
   */
  callableUsers: Record<number, boolean>;

  constructor(globalStore, userStore) {
    this.globalStore = globalStore;
    this.userStore = userStore;
    this.webSocketService = new WebSocketService();
    this.onlineUsers = {};
    this.callableUsers = {};
    this.subscriptions = {};
    this.subscriptionRegistrations = {};
    makeObservable(this, {
      onlineUsers: observable,
      callableUsers: observable,
      getOnlineUsers: flow,
      connect: action,
      isUserOnline: action,
      subscribeToConversationUpdates: action,
      subscribeToUsersOnlineStatusUpdates: action,
      subscribeToCallInvite: action,
      subscribeToCallStatus: action,
      unsubscribe: action
    });
  }

  private _getConnectedStompClient(): Promise<Stomp.Client> {
    if (!this.stompClientPromise) {
      throw new Error("STOMP: Tying to use the client before it is created. Make sure to websocket is connected before subscribe");
    }
    return this.stompClientPromise;
  }

  async connect(): Promise<Stomp.Client> {

    const userId = this.userStore?.userInformation?.userId;

    if (!userId) {
      throw new Error('STOMP: user session is required for connecting to websockets');
    }

    if (this.stompClientPromise) {
      console.warn('STOMP: Application is trying to connect to websocket again. Returning same client instance.');
      return this.stompClientPromise;
    }

    // Resolved before the promise executor because that callback cannot await, and the token has
    // to be in hand by the time the CONNECT frame is written.
    const jwtToken: string | null =
      (await _getBaseStoreInstance()?.authenticationStore?.getJwtToken?.(false)) ?? null;
    if (!jwtToken) {
      console.warn('STOMP: connecting without a token — the server will log this as unauthenticated');
    }

    this.stompClientPromise = new Promise((resolve, reject) => {
      console.log('STOMP: Connecting');
      clearTimeout(this.timeoutId);
      let restApiBaseURL = '';
      if (isMobileApp() && !isMobileAppLocalHost()) {
        restApiBaseURL = (window as any).MOBILE_UI_GLOBAL_DATA.restApiBaseURL;
      }
      if (isElectronApp() && !isElectronAppLocalHost()) {
        restApiBaseURL = (window as any).CHAT_UI_GLOBAL_DATA.restApiBaseURL;
      }
      /*
       * Tell the server which app this session belongs to.
       *
       * Presence in Meet means "could answer a call", not merely "is connected": ringing goes
       * through the Electron main process, so a session anywhere else receives the invite and has
       * nowhere to show it. Only `loanchat-desktop` is treated as callable — see WebSocketClient
       * in vendor-service.
       *
       * isElectronApp() is a build-time flag, which is sound here because the roc-chat bundle is
       * only ever packaged into the desktop app and never served to a browser. The one exception
       * is `npm run start-roc-chat`, which serves it on :9013 for local development with the same
       * flag set — a browser tab there reports itself as callable.
       */
      const clientApp = isElectronApp() ? 'loanchat-desktop' : isMobileApp() ? 'mobile' : 'web';
      var sock = new SockJS(restApiBaseURL + '/api/v1/vendor/public/websocket-connect?userId=' + userId + '&client=' + clientApp);
      const client = Stomp.over(sock);
      sock.onopen = function () { }
      /*
       * The handshake runs over SockJS, which cannot set an Authorization header, so the server
       * has only the userId query parameter to go on — it cannot tell whether this connection
       * really belongs to that user. The STOMP CONNECT frame does carry headers, so the token
       * goes here and the server validates it there. Without it the socket still opens today, but
       * the server logs it as unauthenticated and will reject it once enforcement is switched on.
       */
      client.connect(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}, (frame) => {
        console.log('STOMP: Connected');
        console.log('STOMP: ' + frame);
        resolve(client);
      }, (error) => {
        this.stompFailureCallback(error);
        reject();
      });
    });
    return this.stompClientPromise;
  }

  private disconnect() {
    console.log("STOMP: Disconnecting");
    this.stompClientPromise?.then(client => {
      if (client.connected) {
        try {
          client.disconnect(() => {
            console.log("STOMP: Disconnected");
          });
        } catch (e) {
          console.error("STOMP: Error disconnecting");
          console.error(e);
        }
      }
    })
  }

  *getOnlineUsers() {
    try {
      let response: ApiResponse = yield this.webSocketService.getOnlineUsers();
      const users = response.data.data as UserOnlineStatusNotification[];
      let online = {};
      let callable = {};
      users.forEach(u => {
        online[u.userId] = true;
        callable[u.userId] = !!u.callable;
      });
      this.onlineUsers = online;
      this.callableUsers = callable;
    } catch (e) {
      console.error("Error fetching online users list");
      this.onlineUsers = {};
      this.callableUsers = {};
    }
  }

  isUserOnline(userId: number): boolean {
    return this.onlineUsers[userId] ?? false;
  }

  /**
   * False unless the server has said otherwise, so a call is never offered on an assumption — an
   * unknown user and an uncallable one are treated alike.
   *
   * Deliberately not registered as an action. MobX actions run untracked, so an observer calling
   * this during render would read callableUsers without subscribing to it, and would never
   * re-render when someone opens or closes the desktop app. isUserOnline above carries that
   * annotation and that latent problem; nothing calls it, which is why it has not bitten yet.
   */
  isUserCallable(userId: number): boolean {
    return this.callableUsers[userId] ?? false;
  }

  async subscribeToConversationUpdates(onMessageReceived?: (notification: ConversationUpdatesNotification) => void): Promise<Stomp.Subscription> {
    const subscription = this._getConnectedStompClient().then((stompClient) => {
      return stompClient.subscribe(SubscriptionTopic.TOPIC_CONVERSATION_MESSAGE_UPDATES, (message) => {
        const notification: {
          userIdentity: number,
          twilioIdentity: string,
          conversationSid: string,
          hasMentions: boolean,
          lastMentionedDate: string,
        } = JSON.parse(message.body);
        console.log('STOMP: notification', notification);
        onMessageReceived && onMessageReceived(notification);
      });
    }).then((subscription) => {
      this.registerSubscription(subscription, {
        subscriptionTopic: SubscriptionTopic.TOPIC_CONVERSATION_MESSAGE_UPDATES,
        callBack: onMessageReceived
      });
      return subscription;
    });
    return subscription;
  }

  async subscribeToUsersOnlineStatusUpdates(onMessageReceived?: (userOnlineStatus: UserOnlineStatusNotification) => void): Promise<Stomp.Subscription> {
    this.getOnlineUsers();
    const subscription = this._getConnectedStompClient().then((stompClient) => {
      return stompClient.subscribe(SubscriptionTopic.TOPIC_USER_ONLINE_STATUS_UPDATES, (message) => {
        const userOnlineStatus: UserOnlineStatusNotification = JSON.parse(message.body);
        console.log('STOMP: userOnlineStatus', userOnlineStatus);
        if (userOnlineStatus.online) {
          this.onlineUsers[userOnlineStatus.userId] = true;
        } else {
          delete this.onlineUsers[userOnlineStatus.userId];
        }
        /*
         * Tracked separately rather than inferred from online, because the two move apart: a user
         * who closes the desktop app with a browser tab still open stays online and stops being
         * callable. The server publishes on every disconnect for exactly that case.
         */
        if (userOnlineStatus.callable) {
          this.callableUsers[userOnlineStatus.userId] = true;
        } else {
          delete this.callableUsers[userOnlineStatus.userId];
        }
        onMessageReceived && onMessageReceived(userOnlineStatus);
      });
    }).then((subscription) => {
      this.registerSubscription(subscription, {
        subscriptionTopic: SubscriptionTopic.TOPIC_USER_ONLINE_STATUS_UPDATES,
        callBack: onMessageReceived
      });
      return subscription;
    });
    return subscription;
  }

  /** Someone is ringing this user. The only signal that arrives with the Meet window closed. */
  async subscribeToCallInvite(onMessageReceived?: (invite: CallInviteNotification) => void): Promise<Stomp.Subscription> {
    const subscription = this._getConnectedStompClient().then((stompClient) => {
      return stompClient.subscribe(SubscriptionTopic.TOPIC_CALL_INVITE, (message) => {
        const invite: CallInviteNotification = JSON.parse(message.body);
        console.log('STOMP: callInvite', invite);
        onMessageReceived && onMessageReceived(invite);
      });
    }).then((subscription) => {
      this.registerSubscription(subscription, {
        subscriptionTopic: SubscriptionTopic.TOPIC_CALL_INVITE,
        callBack: onMessageReceived
      });
      return subscription;
    });
    return subscription;
  }

  /** How an invite resolved — lets a ringing or calling window stop. */
  async subscribeToCallStatus(onMessageReceived?: (status: CallStatusNotification) => void): Promise<Stomp.Subscription> {
    const subscription = this._getConnectedStompClient().then((stompClient) => {
      return stompClient.subscribe(SubscriptionTopic.TOPIC_CALL_STATUS, (message) => {
        const status: CallStatusNotification = JSON.parse(message.body);
        console.log('STOMP: callStatus', status);
        onMessageReceived && onMessageReceived(status);
      });
    }).then((subscription) => {
      this.registerSubscription(subscription, {
        subscriptionTopic: SubscriptionTopic.TOPIC_CALL_STATUS,
        callBack: onMessageReceived
      });
      return subscription;
    });
    return subscription;
  }

  unsubscribe(subscriptionTopic: SubscriptionTopic) {
    this.unRegisterSubscription(subscriptionTopic);
  }

  private stompFailureCallback(error) {
    console.error('STOMP: Connection Failure.', error);
    this.stompClientPromise = undefined;
    this.timeoutId = setTimeout(() => {
      this.connect().then((client) => {
        Object.keys(this.subscriptionRegistrations).forEach((x: SubscriptionTopic) => {
          if (x == SubscriptionTopic.TOPIC_CONVERSATION_MESSAGE_UPDATES) {
            this.subscribeToConversationUpdates(this.subscriptionRegistrations[x].callBack);
          } else if (x == SubscriptionTopic.TOPIC_USER_ONLINE_STATUS_UPDATES) {
            this.subscribeToUsersOnlineStatusUpdates(this.subscriptionRegistrations[x].callBack);
          } else if (x == SubscriptionTopic.TOPIC_CALL_INVITE) {
            this.subscribeToCallInvite(this.subscriptionRegistrations[x].callBack);
          } else if (x == SubscriptionTopic.TOPIC_CALL_STATUS) {
            this.subscribeToCallStatus(this.subscriptionRegistrations[x].callBack);
          }
        })
      });
    }, 30000);
    console.log('STOMP: Reconnecting in 30 seconds');
  }

  private registerSubscription(subscription: Subscription, subscriptionRegistration: SubscriptionRegistrationType) {
    /*
     * One subscription per topic, which is what this map has always claimed and never enforced.
     *
     * Registering a second one overwrote the entry and left the first STOMP subscription live,
     * so every message on that topic was delivered twice — once per orphaned subscription, and
     * the orphan could never be unsubscribed again because nothing held a reference to it. The
     * callers re-subscribe more than once in practice: the effect that sets this up depends on
     * userInformation, which is a MobX object whose identity changes.
     *
     * It went unnoticed because duplicate delivery was harmless for everything on these topics —
     * a ring window is focused rather than opened twice, an online-status update is idempotent.
     * It stopped being harmless when a call invite started carrying a decision: the second copy
     * saw the first copy's own state and answered the call busy against itself.
     */
    const previous = this.subscriptions[subscriptionRegistration.subscriptionTopic];
    if (previous && previous !== subscription) {
      try {
        previous.unsubscribe();
      } catch (e) {
        // Already gone with a dropped connection, which is the outcome we wanted anyway.
        console.warn('STOMP: could not unsubscribe the previous subscription', e);
      }
    }
    this.subscriptions[subscriptionRegistration.subscriptionTopic] = subscription;
    this.subscriptionRegistrations[subscriptionRegistration.subscriptionTopic] = subscriptionRegistration
  }

  private unRegisterSubscription(subscriptionTopic: SubscriptionTopic) {
    this.subscriptions[subscriptionTopic]?.unsubscribe();
    delete this.subscriptions[subscriptionTopic];
    delete this.subscriptionRegistrations[subscriptionTopic];
  }

}